import SubvencionsGrantActions from './SubvencionsGrantActions';

function formatSourceType(sourceType) {
    const labels = {
        tramits_detail: 'Tràmits Gencat',
        dogc_specific: 'DOGC · Bases específiques',
        dogc_general: 'DOGC · Bases generals',
        dogc_call: 'DOGC · Convocatòria',
        barcelona_detail: 'Ajuntament de Barcelona · Fitxa de subvenció',
        barcelona_document: 'Ajuntament de Barcelona · Document oficial',
        cido_detail: 'CIDO · Fitxa de subvenció',
        bopb_document: 'BOPB · Document oficial',
        bopg_document: 'BOPG · Document oficial',
        bopt_document: 'BOPT · Document oficial',
        dogc_document: 'DOGC · Document oficial',
        cido_document: 'CIDO · Document oficial',
    };

    return labels[sourceType] || sourceType;
}

function decodeHtmlEntities(text) {
    if (!text) return text;
    if (typeof document === 'undefined') return text;
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
}

function decodeArray(values) {
    return Array.isArray(values) ? values.map(value => decodeHtmlEntities(value)) : values;
}

function decodeGuideSection(section) {
    if (!section) return section;

    return {
        ...section,
        paragraphs: decodeArray(section.paragraphs),
        bullets: decodeArray(section.bullets),
        groups: Array.isArray(section.groups)
            ? section.groups.map(group => ({
                ...group,
                title: decodeHtmlEntities(group.title),
                paragraphs: decodeArray(group.paragraphs),
                bullets: decodeArray(group.bullets),
            }))
            : section.groups,
        steps: Array.isArray(section.steps)
            ? section.steps.map(step => ({
                ...step,
                title: decodeHtmlEntities(step.title),
                description: decodeHtmlEntities(step.description),
                paragraphs: decodeArray(step.paragraphs),
                bullets: decodeArray(step.bullets),
            }))
            : section.steps,
        items: Array.isArray(section.items)
            ? section.items.map(item => ({
                ...item,
                label: decodeHtmlEntities(item.label),
                phone: decodeHtmlEntities(item.phone),
                email: decodeHtmlEntities(item.email),
            }))
            : section.items,
    };
}

function StatusBadge({ status, statusLabel }) {
    const colors = {
        open: { background: '#2e7d32', color: '#fff' },
        pending: { background: '#ef6c00', color: '#fff' },
        waiting: { background: '#546e7a', color: '#fff' },
        closed: { background: '#e0e0e0', color: '#424242' },
        unknown: { background: '#e0e0e0', color: '#424242' },
    };

    const palette = colors[status] || colors.unknown;

    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '6px 12px',
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.2,
            backgroundColor: palette.background,
            color: palette.color,
        }}>
            {statusLabel}
        </span>
    );
}

const ACCENT_PALETTES = {
    default: {
        border: '#e0e0e0',
        headerColor: '#1f2937',
        topBar: null,
        bulletMarker: null,
        bulletMarkerColor: null,
    },
    eligible: {
        border: 'rgba(16, 185, 129, 0.28)',
        headerColor: '#047857',
        topBar: '#10b981',
        bulletMarker: '✓',
        bulletMarkerColor: '#047857',
    },
    excluded: {
        border: 'rgba(239, 68, 68, 0.28)',
        headerColor: '#b91c1c',
        topBar: '#ef4444',
        bulletMarker: '✗',
        bulletMarkerColor: '#b91c1c',
    },
};

function AccentBullets({ bullets, palette, keyPrefix }) {
    if (!bullets?.length) return null;

    if (!palette.bulletMarker) {
        return (
            <ul style={{ margin: 0, paddingLeft: 20, color: '#374151', lineHeight: 1.65 }}>
                {bullets.map((bullet, index) => (
                    <li key={`${keyPrefix}-${index}`} style={{ marginBottom: 8 }}>
                        {bullet}
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', color: '#374151', lineHeight: 1.65 }}>
            {bullets.map((bullet, index) => (
                <li
                    key={`${keyPrefix}-${index}`}
                    style={{
                        position: 'relative',
                        paddingLeft: 26,
                        marginBottom: 10,
                    }}
                >
                    <span style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 18,
                        height: 18,
                        lineHeight: '18px',
                        textAlign: 'center',
                        color: palette.bulletMarkerColor,
                        fontWeight: 800,
                        fontSize: 14,
                    }}>
                        {palette.bulletMarker}
                    </span>
                    {bullet}
                </li>
            ))}
        </ul>
    );
}

function GuideSection({ title, section, accent = 'default' }) {
    if (!section) return null;

    const hasParagraphs = Array.isArray(section.paragraphs) && section.paragraphs.length > 0;
    const hasBullets = Array.isArray(section.bullets) && section.bullets.length > 0;
    const hasGroups = Array.isArray(section.groups) && section.groups.length > 0;
    const hasSteps = Array.isArray(section.steps) && section.steps.length > 0;
    const hasContacts = Array.isArray(section.items) && section.items.length > 0;

    if (!hasParagraphs && !hasBullets && !hasGroups && !hasSteps && !hasContacts) {
        return null;
    }

    const palette = ACCENT_PALETTES[accent] || ACCENT_PALETTES.default;

    return (
        <section style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 24,
            paddingTop: palette.topBar ? 22 : 24,
            border: `1px solid ${palette.border}`,
            position: 'relative',
            overflow: 'hidden',
        }}>
            {palette.topBar ? (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    backgroundColor: palette.topBar,
                }} />
            ) : null}

            <h3 style={{
                marginTop: 0,
                marginBottom: 16,
                color: palette.headerColor,
                fontSize: 18,
            }}>
                {title}
            </h3>

            {hasParagraphs ? section.paragraphs.map((paragraph, index) => (
                <p
                    key={`${title}-p-${index}`}
                    style={{ marginTop: 0, marginBottom: 12, color: '#4b5563', lineHeight: 1.65 }}
                >
                    {paragraph}
                </p>
            )) : null}

            {hasBullets ? (
                <AccentBullets
                    bullets={section.bullets}
                    palette={palette}
                    keyPrefix={`${title}-b`}
                />
            ) : null}

            {hasGroups ? section.groups.map((group, index) => (
                <div key={`${title}-g-${index}`} style={{
                    marginTop: index === 0 ? 18 : 22,
                    padding: '14px 16px',
                    borderRadius: 8,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#111827', fontSize: 15 }}>
                        {group.title}
                    </h4>
                    {group.paragraphs?.length ? group.paragraphs.map((paragraph, paragraphIndex) => (
                        <p
                            key={`${title}-g-${index}-p-${paragraphIndex}`}
                            style={{ marginTop: 0, marginBottom: 10, color: '#4b5563', lineHeight: 1.65 }}
                        >
                            {paragraph}
                        </p>
                    )) : null}
                    {group.bullets?.length ? (
                        <AccentBullets
                            bullets={group.bullets}
                            palette={palette}
                            keyPrefix={`${title}-g-${index}-b`}
                        />
                    ) : null}
                </div>
            )) : null}

            {hasSteps ? (
                <div style={{ display: 'grid', gap: 16 }}>
                    {section.steps.map((step, index) => (
                        <div
                            key={`${title}-s-${index}`}
                            style={{
                                borderRadius: 8,
                                border: '1px solid #e0e0e0',
                                backgroundColor: '#f5f5f5',
                                padding: 18,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                <span style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 999,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#14b8a6',
                                    color: '#fff',
                                    fontSize: 13,
                                    fontWeight: 700,
                                }}>
                                    {index + 1}
                                </span>
                                <h4 style={{ margin: 0, color: '#0f172a', fontSize: 15 }}>{step.title}</h4>
                            </div>
                            {step.description ? (
                                <p style={{ marginTop: 0, marginBottom: 10, color: '#4b5563', lineHeight: 1.65 }}>
                                    {step.description}
                                </p>
                            ) : null}
                            {step.paragraphs?.map((paragraph, paragraphIndex) => (
                                <p
                                    key={`${title}-s-${index}-p-${paragraphIndex}`}
                                    style={{ marginTop: 0, marginBottom: 10, color: '#4b5563', lineHeight: 1.65 }}
                                >
                                    {paragraph}
                                </p>
                            ))}
                            {step.bullets?.length ? (
                                <AccentBullets
                                    bullets={step.bullets}
                                    palette={ACCENT_PALETTES.default}
                                    keyPrefix={`${title}-s-${index}-b`}
                                />
                            ) : null}
                        </div>
                    ))}
                </div>
            ) : null}

            {hasContacts ? (
                <div style={{ display: 'grid', gap: 12 }}>
                    {section.items.map((item, index) => (
                        <div
                            key={`${title}-c-${index}`}
                            style={{
                                padding: 16,
                                borderRadius: 8,
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            <div style={{ fontWeight: 600, color: '#111827', marginBottom: 6 }}>{item.label}</div>
                            {item.phone ? <div style={{ color: '#4b5563' }}>{item.phone}</div> : null}
                            {item.email ? (
                                <a href={`mailto:${item.email}`} style={{ color: '#0f766e', textDecoration: 'none' }}>
                                    {item.email}
                                </a>
                            ) : null}
                        </div>
                    ))}
                </div>
            ) : null}
        </section>
    );
}

function KeyFactsStrip({ facts }) {
    if (!Array.isArray(facts) || facts.length === 0) return null;

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
        }}>
            {facts.map((fact, index) => (
                <div
                    key={`keyfact-${index}`}
                    style={{
                        backgroundColor: '#fff',
                        padding: '14px 18px',
                        borderRadius: 8,
                        border: '1px solid #e0e0e0',
                        borderLeft: '3px solid #0f766e',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }}
                >
                    <span style={{
                        fontSize: 11,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        color: '#6b7280',
                        fontWeight: 700,
                    }}>
                        {fact.label}
                    </span>
                    <span style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: '#111827',
                        lineHeight: 1.3,
                    }}>
                        {fact.value}
                    </span>
                </div>
            ))}
        </div>
    );
}

const SECTION_CONFIG = [
    { key: 'eligibility', title: "Qui s'hi pot presentar" },
    { key: 'modalities', title: 'Modalitats' },
    { key: 'budget_rules', title: 'Quant es pot rebre' },
    { key: 'eligible_expenses', title: "Què cobreix l'ajut", accent: 'eligible' },
    { key: 'excluded_activities', title: 'Què NO cobreix', accent: 'excluded' },
    { key: 'requirements', title: 'Requisits i límits' },
    { key: 'documents', title: 'Documents a preparar' },
    { key: 'evaluation_criteria', title: 'Com es valora' },
    { key: 'post_award_steps', title: 'Del formulari al cobrament' },
    { key: 'justification', title: 'Justificació posterior' },
    { key: 'contacts', title: 'Contactes' },
];

function SubvencionsGrantDetail({ data, requestedYear }) {
    const { program, edition, history = [], guide, sources = [] } = data;
    const isExplicitYearView = Boolean(requestedYear);
    const isTimelessGuide = guide?.presentation?.timeless === true;
    const isPartialGuide = edition.guideStatus === 'partial';
    const isGuideMissing = edition.guideStatus === 'none';
    const decodedSummary = decodeHtmlEntities(edition.summary);

    const decodedGuide = SECTION_CONFIG.reduce((acc, { key }) => {
        acc[key] = decodeGuideSection(guide?.[key]);
        return acc;
    }, {});
    const decodedOverview = decodeGuideSection(guide?.overview);
    const heroSummary = decodedOverview?.paragraphs?.[0] || decodedSummary;
    const keyFacts = Array.isArray(guide?.keyFacts) ? guide.keyFacts : [];

    return (
        <div style={{ display: 'grid', gap: 20 }}>
            <section style={{
                backgroundColor: '#fff',
                borderRadius: 8,
                padding: 28,
                border: '1px solid #e0e0e0',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ maxWidth: 760 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                            <span style={{
                                display: 'inline-flex',
                                padding: '6px 10px',
                                borderRadius: 4,
                                backgroundColor: '#f5f5f5',
                                color: '#374151',
                                border: '1px solid #e0e0e0',
                                fontSize: 13,
                                fontWeight: 700,
                                letterSpacing: 0.4,
                            }}>
                                {program.code || program.canonicalSlug.toUpperCase()}
                            </span>
                            {!isTimelessGuide ? (
                                <StatusBadge status={edition.status} statusLabel={edition.statusLabel} />
                            ) : null}
                            <span style={{ fontSize: 13, color: '#6b7280' }}>
                                {isTimelessGuide
                                    ? 'Guia per a colles castelleres'
                                    : (isExplicitYearView ? `Convocatòria ${edition.callYear}` : 'Guia vigent')}
                            </span>
                            {edition.sourceAgency ? (
                                <span style={{ fontSize: 13, color: '#6b7280' }}>
                                    {edition.sourceAgency}
                                </span>
                            ) : null}
                        </div>

                        <h2 style={{ marginTop: 0, marginBottom: 12, lineHeight: 1.25, color: '#111827' }}>
                            {edition.title}
                        </h2>

                        <p style={{ marginTop: 0, marginBottom: 10, fontSize: 16, lineHeight: 1.65, color: '#4b5563' }}>
                            {heroSummary}
                        </p>

                        {!isTimelessGuide ? (
                            <p style={{ marginTop: 0, marginBottom: 0, fontSize: 14, color: '#6b7280' }}>
                                {edition.dateRangeLabel}
                                {edition.closeTimeLabel ? ` (tanca a les ${edition.closeTimeLabel})` : ''}
                            </p>
                        ) : null}
                    </div>

                    <SubvencionsGrantActions
                        grant={edition}
                        color="#0f766e"
                        variant="detail"
                        showApply={!isTimelessGuide}
                    />
                </div>
            </section>

            <KeyFactsStrip facts={keyFacts} />

            {!isTimelessGuide && edition.isHistorical ? (
                <section style={{
                    backgroundColor: '#fff7ed',
                    borderRadius: 16,
                    padding: 18,
                    border: '1px solid rgba(249, 115, 22, 0.18)',
                    color: '#9a3412',
                }}>
                    Estàs consultant la convocatòria de {edition.callYear}. Revisa sempre les dates i les bases oficials perquè els requisits poden canviar cada any.
                </section>
            ) : null}

            {isPartialGuide || isGuideMissing ? (
                <section style={{
                    backgroundColor: '#eff6ff',
                    borderRadius: 16,
                    padding: 18,
                    border: '1px solid rgba(37, 99, 235, 0.14)',
                    color: '#1d4ed8',
                }}>
                    {isPartialGuide
                        ? 'La guia s’ha pogut construir només en part amb les fonts oficials disponibles. Contrasta els punts finals amb les bases i el tràmit oficial.'
                        : 'Encara no hi ha una guia estructurada per a aquesta convocatòria. Pots consultar igualment les fonts oficials i el tràmit complet.'}
                </section>
            ) : null}

            {!isTimelessGuide && history.length > 1 ? (
                <section style={{
                    backgroundColor: '#fff',
                    borderRadius: 8,
                    padding: 20,
                    border: '1px solid #e0e0e0',
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: 12, color: '#1f2937', fontSize: 15 }}>
                        Altres convocatòries
                    </h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        {history.map((entry) => {
                            const isSelected = Number(entry.callYear) === Number(edition.callYear);
                            return (
                                <a
                                    key={entry.callYear}
                                    href={`/subvencions/${entry.canonicalSlug}/${entry.callYear}`}
                                    style={{
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                        padding: '10px 14px',
                                        borderRadius: 8,
                                        border: isSelected ? '1px solid #0f766e' : '1px solid #d1d5db',
                                        backgroundColor: isSelected ? '#ecfdf5' : '#fff',
                                        color: '#111827',
                                        textDecoration: 'none',
                                        minWidth: 140,
                                    }}
                                >
                                    <strong>{entry.callYear}</strong>
                                    <span style={{ fontSize: 13, color: '#6b7280' }}>
                                        {entry.dateRangeLabel || entry.status}
                                    </span>
                                </a>
                            );
                        })}
                    </div>
                </section>
            ) : null}

            <div style={{ display: 'grid', gap: 14 }}>
                {SECTION_CONFIG.map(({ key, title, accent }) => (
                    <GuideSection
                        key={key}
                        title={title}
                        section={decodedGuide[key]}
                        accent={accent}
                    />
                ))}
            </div>

            <section style={{
                backgroundColor: '#fff',
                borderRadius: 8,
                padding: 22,
                border: '1px solid #e0e0e0',
            }}>
                <h3 style={{ marginTop: 0, marginBottom: 14, color: '#1f2937', fontSize: 16 }}>Fonts oficials</h3>
                <div style={{ display: 'grid', gap: 10 }}>
                    {sources.map((source, index) => (
                        <a
                            key={`${source.sourceType}-${source.externalId || index}`}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'block',
                                padding: 14,
                                borderRadius: 8,
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e0e0e0',
                                color: '#111827',
                                textDecoration: 'none',
                            }}
                        >
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>{decodeHtmlEntities(source.title)}</div>
                            <div style={{ fontSize: 13, color: '#6b7280' }}>
                                {formatSourceType(source.sourceType)}
                                {!isTimelessGuide && source.publishedAt ? ` · publicat el ${source.publishedAt}` : ''}
                            </div>
                        </a>
                    ))}
                </div>
            </section>

            {!isTimelessGuide && (edition.applyUrl || edition.sourceUrl) ? (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
                    <SubvencionsGrantActions
                        grant={edition}
                        color="#14b8a6"
                        variant="detail"
                        applyLabel="Anar al tràmit oficial"
                        showInfo={false}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default SubvencionsGrantDetail;
